import { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import logger from "../utils/logger";

const JESS_AI_CHANGE_EVENT = "jessAIChange";

interface UseAIToggleReturn {
  isAIEnabled: boolean;
  toggleAI: () => Promise<void>;
  isSwitchVisible: boolean;
  toggleVisibility: () => void;
  loading: boolean;
}

export function useAIToggle(): UseAIToggleReturn {
  const [isAIEnabled, setIsAIEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSwitchVisible, setIsSwitchVisible] = useState<boolean>(() => {
    const savedVisibility = localStorage.getItem("switchVisibility");
    return savedVisibility ? JSON.parse(savedVisibility) : true;
  });

  useEffect(() => {
    const cachedAIStatus = localStorage.getItem("isAIEnabled");
    if (cachedAIStatus !== null) {
      try {
        setIsAIEnabled(JSON.parse(cachedAIStatus));
      } catch (e) {
        logger.error("Failed to parse cached AI status:", e);
      }
    }
    setLoading(false);

    // Sync with other instances on the same page
    const handler = (e: Event) => {
      setIsAIEnabled((e as CustomEvent).detail.isAIEnabled);
    };
    window.addEventListener(JESS_AI_CHANGE_EVENT, handler);
    return () => window.removeEventListener(JESS_AI_CHANGE_EVENT, handler);
  }, []);

  const toggleAI = useCallback(async () => {
    const newStatus = !isAIEnabled;
    try {
      await api.post("/api/toggle-ai", { isAIEnabled: newStatus });
      setIsAIEnabled(newStatus);
      localStorage.setItem("isAIEnabled", JSON.stringify(newStatus));
      window.dispatchEvent(
        new CustomEvent(JESS_AI_CHANGE_EVENT, {
          detail: { isAIEnabled: newStatus },
        }),
      );
    } catch (error) {
      logger.error("Failed to update toggle status:", error);
    }
  }, [isAIEnabled]);

  const toggleVisibility = useCallback(() => {
    setIsSwitchVisible((prev: boolean) => {
      const newState = !prev;
      localStorage.setItem("switchVisibility", JSON.stringify(newState));
      return newState;
    });
  }, []);

  return {
    isAIEnabled,
    toggleAI,
    isSwitchVisible,
    toggleVisibility,
    loading,
  };
}
